import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const TermsOfService = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-gray-100 dark:bg-slate-900 py-12 px-4">
      <div className="container mx-auto max-w-4xl">
        <button
          onClick={() => navigate('/')}
          className="flex items-center text-blue-600 hover:text-blue-700 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </button>
        
        <Card>
          <CardHeader>
            <CardTitle className="text-3xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Terms of Service</CardTitle>
            <p className="text-slate-600 dark:text-slate-400 text-sm mt-2">Last Updated: January 26, 2025</p>
          </CardHeader>
          <CardContent className="prose dark:prose-invert max-w-none">
            <p className="text-slate-700 dark:text-slate-300">
              These Terms of Service ("Terms") govern your use of SmartPDF Tools ("Service") operated by SmartPDF Tools ("we," "us," or "our"). By accessing or using our Service, you agree to be bound by these Terms.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">1. Acceptance of Terms</h2>
            <p className="text-slate-700 dark:text-slate-300">
              By creating an account, accessing, or using SmartPDF Tools, you acknowledge that you have read, understood, and agree to be bound by these Terms. If you do not agree, you may not use the Service.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">2. Eligibility</h2>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300">
              <li>You must be at least 18 years old (or 13 with parental consent)</li>
              <li>You must provide accurate, current, and complete information</li>
              <li>You are responsible for maintaining account security</li>
              <li>One person or entity per account</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">3. Account Registration</h2>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300">
              <li><strong>Free Account:</strong> Required to use any PDF tools</li>
              <li><strong>Email Verification:</strong> Valid email required for password recovery</li>
              <li><strong>Password Security:</strong> You are responsible for keeping your password secure</li>
              <li><strong>Account Responsibility:</strong> You are responsible for all activity under your account</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">4. Service Plans</h2>
            
            <h3 className="text-xl font-semibold mt-6 mb-3">4.1 Free Plan</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300">
              <li>3 PDF operations per day</li>
              <li>Access to all PDF tools (merge, split, compress, convert)</li>
              <li>Ad-supported experience</li>
              <li>Files deleted after 1 hour</li>
            </ul>

            <h3 className="text-xl font-semibold mt-6 mb-3">4.2 Pro Plan ($5/month)</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300">
              <li>Unlimited PDF operations</li>
              <li>No advertisements</li>
              <li>Priority processing</li>
              <li>Auto-renews monthly until cancelled</li>
            </ul>

            <h3 className="text-xl font-semibold mt-6 mb-3">4.3 Yearly Plan ($50/year)</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300">
              <li>Unlimited PDF operations</li>
              <li>No advertisements</li>
              <li>Priority processing</li>
              <li>Save $10 vs monthly billing</li>
              <li>Auto-renews yearly until cancelled</li>
            </ul>

            <h3 className="text-xl font-semibold mt-6 mb-3">4.4 Lifetime Plan ($39 one-time)</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300">
              <li>Unlimited PDF operations forever</li>
              <li>No advertisements</li>
              <li>Priority processing</li>
              <li>All future updates included</li>
              <li>One-time payment, no recurring charges</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">5. Payment Terms</h2>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300">
              <li><strong>Payment Processing:</strong> All payments processed securely through Stripe</li>
              <li><strong>Billing:</strong> Charges occur at time of purchase and renewal</li>
              <li><strong>Auto-Renewal:</strong> Subscriptions auto-renew unless cancelled</li>
              <li><strong>Cancellation:</strong> Cancel anytime; access continues until end of billing period</li>
              <li><strong>Price Changes:</strong> We reserve the right to change prices with 30 days notice</li>
              <li><strong>Taxes:</strong> Prices exclude applicable taxes (added at checkout)</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">6. Use of PDF Tools</h2>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300">
              <li><strong>Supported Operations:</strong> Merge, split, compress, PDF to JPG, PDF to Word, Word to PDF, Images to PDF</li>
              <li><strong>File Limits:</strong> Maximum file size may vary by tool</li>
              <li><strong>Processing Time:</strong> Varies based on file size and complexity</li>
              <li><strong>File Deletion:</strong> Processed files automatically deleted after 1 hour</li>
              <li><strong>No Guarantee:</strong> We strive for accuracy but do not guarantee perfect output</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">7. Intellectual Property</h2>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300">
              <li><strong>Our Content:</strong> All Service content, design, logos, and trademarks are owned by SmartPDF Tools</li>
              <li><strong>Your Files:</strong> You retain all rights to files you upload</li>
              <li><strong>License to Process:</strong> You grant us temporary license to process your files</li>
              <li><strong>No Content Mining:</strong> We do not use your files for AI training or analysis</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">8. User Responsibilities</h2>
            <p className="text-slate-700 dark:text-slate-300">You agree to:</p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300">
              <li>Use the Service only for lawful purposes</li>
              <li>Not upload files you do not have rights to process</li>
              <li>Not attempt to hack, reverse engineer, or abuse the Service</li>
              <li>Not share account credentials</li>
              <li>Respect usage limits (3 files/day for free users)</li>
              <li>Comply with our <a href="/acceptable-use-policy" className="text-blue-600 hover:text-blue-700">Acceptable Use Policy</a></li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">9. Prohibited Activities</h2>
            <p className="text-slate-700 dark:text-slate-300">You may NOT:</p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300">
              <li>Upload illegal, harmful, or malicious content</li>
              <li>Upload copyrighted material without permission</li>
              <li>Use automated scripts or bots to abuse the Service</li>
              <li>Resell or redistribute our Service</li>
              <li>Attempt to bypass usage limits or security measures</li>
              <li>Spam, harass, or harm other users</li>
              <li>Interfere with Service operations</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">10. Disclaimer of Warranties</h2>
            <p className="text-slate-700 dark:text-slate-300">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300 mt-3">
              <li>Merchantability, fitness for a particular purpose</li>
              <li>Accuracy, reliability, or availability</li>
              <li>Error-free or uninterrupted operation</li>
              <li>Security or freedom from viruses</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">11. Limitation of Liability</h2>
            <p className="text-slate-700 dark:text-slate-300">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300 mt-3">
              <li>Indirect, incidental, special, or consequential damages</li>
              <li>Loss of profits, data, or goodwill</li>
              <li>Service interruptions or errors</li>
              <li>Unauthorized access to your account</li>
              <li>Third-party actions</li>
            </ul>
            <p className="text-slate-700 dark:text-slate-300 mt-3">
              Our total liability shall not exceed the amount paid by you in the past 12 months, or $100, whichever is less.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">12. Indemnification</h2>
            <p className="text-slate-700 dark:text-slate-300">
              You agree to indemnify and hold harmless SmartPDF Tools from claims, damages, losses, and expenses (including legal fees) arising from:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300 mt-3">
              <li>Your use of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of third-party rights</li>
              <li>Your uploaded content</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">13. Termination</h2>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 dark:text-slate-300">
              <li><strong>By You:</strong> You may delete your account anytime</li>
              <li><strong>By Us:</strong> We may suspend or terminate accounts that violate these Terms</li>
              <li><strong>Effect:</strong> Upon termination, your access ends and data may be deleted</li>
              <li><strong>Survival:</strong> Sections 7, 10, 11, 12, and 14 survive termination</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">14. Governing Law</h2>
            <p className="text-slate-700 dark:text-slate-300">
              These Terms are governed by the laws of the jurisdiction where SmartPDF Tools operates. Disputes shall be resolved in the courts of that jurisdiction.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">15. Changes to Terms</h2>
            <p className="text-slate-700 dark:text-slate-300">
              We reserve the right to modify these Terms at any time. Material changes will be notified via email or website notice 30 days in advance. Continued use after changes constitutes acceptance.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">16. Contact Information</h2>
            <p className="text-slate-700 dark:text-slate-300">
              Questions about these Terms? Contact us:
            </p>
            <ul className="list-none pl-0 space-y-2 text-slate-700 dark:text-slate-300 mt-3">
              <li><strong>Email:</strong> <a href="mailto:support@smrtpdftools.com" className="text-blue-600 hover:text-blue-700">support@smrtpdftools.com</a></li>
              <li><strong>Support:</strong> <a href="mailto:support@smrtpdftools.com" className="text-blue-600 hover:text-blue-700">support@smrtpdftools.com</a></li>
              <li><strong>Website:</strong> <a href="https://smrtpdftools.com" className="text-blue-600 hover:text-blue-700">https://smrtpdftools.com</a></li>
            </ul>

            <div className="mt-12 p-6 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-sm text-slate-700 dark:text-slate-300">
                <strong>By using SmartPDF Tools, you acknowledge that you have read and agree to these Terms of Service.</strong>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TermsOfService;